import { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useRouteError } from 'react-router';

import type { MessageKey } from 'translations';

import { isMessageKey, t } from 'translations/src/LocaleContext';
import { Button, Header } from 'ui';
import { ErrorPageComponent } from 'ui/components/ErrorPageComponent';
import { MainContainer } from 'ui/styles/containers';
import { Link } from 'ui/styles/text';

import { routes } from '../../membership-management-portal/src/routes';

interface ErrorType {
  statusText?: string;
  message?: string;
}

type ErrorPageType = 'notFound' | 'error' | 'errorReturnToHome';

interface ErrorPageProps {
  errorPageType?: ErrorPageType;
  appType: 'hub' | 'signup';
}

const getNewPasswordLink = () =>
  t('membership_hub_unverified_account', {
    membership_hub_unverified_account_link_label: (
      <Link href="/request-password-reset" $fontSize="16px" $decoration="underline">
        {t('membership_hub_unverified_account_link_label')}
      </Link>
    ),
  });

const getLoginLink = () =>
  t('membership_hub_moi_error_signup_required', {
    LinkToLogin: (linkText) => (
      <Link href="/login" $decoration="underline" $fontSize="16px">
        {linkText}
      </Link>
    ),
  });

export const ErrorPage = ({ errorPageType = 'error', appType }: ErrorPageProps) => {
  const navigate = useNavigate();

  let defaultMessageByApp: MessageKey | undefined;

  if (appType === 'hub') {
    defaultMessageByApp =
      errorPageType === 'errorReturnToHome'
        ? 'membership_hub_general_error_return_to_home'
        : 'membership_hub_general_error';
  } else {
    defaultMessageByApp = 'membership_error_unknown';
  }

  const headerKey: MessageKey =
    errorPageType === 'notFound' ? 'membership_error_page_not_found' : 'membership_error_error_occured';
  const defaultMessageKey: MessageKey =
    errorPageType === 'notFound' ? 'membership_error_page_you_are_looking_for_not_exists' : defaultMessageByApp;
  const error = useRouteError() as ErrorType | undefined;
  const errorMessageKey = error?.statusText ?? error?.message ?? defaultMessageKey;

  const intl = useIntl();
  const header = intl.formatMessage({ id: headerKey });
  let message: ReactNode;

  if (isMessageKey(errorMessageKey)) {
    if (errorMessageKey === 'membership_hub_unverified_account') {
      message = getNewPasswordLink();
    } else if (errorMessageKey === 'membership_hub_moi_error_signup_required') {
      message = getLoginLink();
    } else {
      message = intl.formatMessage({ id: errorMessageKey });
    }
  } else {
    message = errorMessageKey;
  }

  return (
    <>
      <Header appType={appType} />
      <MainContainer>
        <ErrorPageComponent
          header={header}
          message={message}
          cta={
            appType === 'hub' && errorPageType === 'errorReturnToHome' ? (
              <Button onClick={() => navigate(routes.index)}>
                {intl.formatMessage({ id: 'membership_hub_return_to_hub_home' })}
              </Button>
            ) : undefined
          }
        />
      </MainContainer>
    </>
  );
};
